import React, { createContext, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import { FormTile } from "../../../../../../Common/Tile";

import SourceForm from "./SourceForm";
import DestinationForm from "./DestinationForm";
import MappingPopup from "../../../../DataExchange/DataExchangeScheduler/MappingPopup";
import AddConnection from "../../../../../DataConnections/AddConnection";
import { JOB_PURPOSES, JOB_PURPOSE_EXCHANGE } from "../../../../utils/JobData";
import { setPrevState } from "../../../../../../../util/common";

import SnowflakeService from "../../../../../../../service/snowflake";
import SQLService from "../../../../../../../service/mysqlService";
import Workspace from "../../../../../../../service/workspace";
import SmallToolbar from "../../../../../../Common/SmallToolbar";

export const DestinationContext = createContext();

const SourceDestination = ({
  exchangeJobState,
  setPreviousState,
  setExchangeJobState,
  restrictedObject,
  previousState,
  fieldLoading,
  loadLocationList,
  handleJobPurposeChange,
  setFieldLoading,
  loadSchemasClusters,
  runTimeState,
}) => {
  const navigate = useNavigate();
  const { delimiters, defaultSourceValues, salesforceDelimiters } = useSelector((state) => state.config);
  const defaultDelimiter = defaultSourceValues.delimiter;

  let { Workspace_id } = useParams();

  const {
    locationList,
    sourceType,
    sourcePath,
    sourcePathError,
    queryError,
    query,
    filename,
    fileType,
    loadingDropBoxValues,
    resetFiles,
    source,
    targetPath,
    destination,
    mapping,
    delimiter,
    customDelimeter,
    hasHeaderRow,
    encoding,
    fieldEnclosedBy,
    groupBy,
    schemaName,
    tableName,
    destinationAction,
    existingTableAction,
    fields,
    isTableLoaded,
    jobPurpose,
  } = exchangeJobState;

  const destinationType = useMemo(
    () =>
      exchangeJobState.locationList
        .find(({ Stage_id }) => Stage_id === exchangeJobState.destination)
        ?.Detail?.type?.toLowerCase() || "",
    [exchangeJobState.destination, exchangeJobState.locationList]
  );

  const loadSnowflakeTables = useCallback(
    async (schema = exchangeJobState.schemaName) => {
      if (["", null, undefined].includes(schema)) return [];
      setFieldLoading(true);
      const snowflake = new SnowflakeService();
      const response = await snowflake.getTables(Workspace_id, schema.toUpperCase());
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) {
        const tables = response.data || [];

        const isTableExist =
          tables?.find((table) => table.toLowerCase() === exchangeJobState.tableName.toLowerCase()) || false;

        let otherProperties = {};
        if (!exchangeJobState.isTableLoaded) {
          otherProperties = {
            destinationAction: isTableExist && exchangeJobState.tableName ? "existing" : "new",
          };
          setPreviousState((prev) => {
            return { ...prev, ...otherProperties };
          });
        }

        setPrevState(setExchangeJobState, {
          schemaName: schema,
          tableName: !exchangeJobState.isTableLoaded ? exchangeJobState.tableName : "",
          showSchemaError: false,
          tableList: tables,
          isFieldsConfigured: schema !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
          isTableLoaded: true,
          ...otherProperties,
        });

        setFieldLoading(false);
        return tables;
      }
      setFieldLoading(false);
      return [];
    },
    [
      Workspace_id,
      exchangeJobState.isFieldsConfigured,
      exchangeJobState.isTableLoaded,
      exchangeJobState.schemaName,
      exchangeJobState.tableName,
      navigate,
      previousState.schemaName,
      setExchangeJobState,
      setFieldLoading,
      setPreviousState,
    ]
  );

  const loadExternalSnowflakeTables = useCallback(
    async (location = destination, database = groupBy, schema = schemaName) => {
      setFieldLoading(true);
      if (!database || !schema || !location) return [];
      const wsService = new Workspace();
      const response = await wsService.getExternalSnowflakeTables(Workspace_id, location, { database, schema });

      if (response.success) {
        const tables = response.data;

        const isTableExist =
          tables.some((table) => table.toLowerCase() === exchangeJobState?.tableName?.toLowerCase()) || false;

        let otherProperties = {};
        if (!exchangeJobState.isTableLoaded) {
          otherProperties = { destinationAction: isTableExist && exchangeJobState.tableName ? "existing" : "new" };
          setPreviousState((prev) => {
            return { ...prev, ...otherProperties };
          });
        }

        setPrevState(setExchangeJobState, {
          groupBy: database,
          schemaName: schema,
          tableName: !exchangeJobState.isTableLoaded ? exchangeJobState.tableName : "",
          showSchemaError: false,
          tableList: tables,
          isFieldsConfigured: schema !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
          isTableLoaded: true,
          ...otherProperties,
        });
      }

      setFieldLoading(false);
      return [];
    },
    [
      Workspace_id,
      destination,
      exchangeJobState.isFieldsConfigured,
      exchangeJobState.isTableLoaded,
      exchangeJobState.tableName,
      groupBy,
      previousState.schemaName,
      schemaName,
      setExchangeJobState,
      setFieldLoading,
      setPreviousState,
    ]
  );

  const loadmysqlTables = useCallback(
    async (schemaName = exchangeJobState.schemaName) => {
      if (["", null, undefined].includes(schemaName)) return [];
      setFieldLoading(true);

      const mysqlService = new SQLService();
      const response = await mysqlService.getMysqlTables(Workspace_id, exchangeJobState.destination, { schemaName });
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) {
        const tables = response.data || [];

        const isTableExist =
          tables?.find((table) => table.toLowerCase() === exchangeJobState.tableName.toLowerCase()) || false;

        let otherProperties = {};
        if (!exchangeJobState.isTableLoaded) {
          otherProperties = {
            destinationAction: isTableExist && exchangeJobState.tableName ? "existing" : "new",
          };
          setPreviousState((prev) => {
            return { ...prev, ...otherProperties };
          });
        }

        setPrevState(setExchangeJobState, {
          schemaName: schemaName,
          tableName: !exchangeJobState.isTableLoaded ? exchangeJobState.tableName : "",
          showSchemaError: false,
          tableList: tables,
          isFieldsConfigured: schemaName !== previousState.schemaName ? false : exchangeJobState.isFieldsConfigured,
          isTableLoaded: true,
          ...otherProperties,
        });

        setFieldLoading(false);
        return tables;
      }

      setFieldLoading(false);
      return [];
    },
    [
      Workspace_id,
      exchangeJobState.destination,
      exchangeJobState.isFieldsConfigured,
      exchangeJobState.isTableLoaded,
      exchangeJobState.schemaName,
      exchangeJobState.tableName,
      navigate,
      previousState.schemaName,
      setExchangeJobState,
      setFieldLoading,
      setPreviousState,
    ]
  );

  useEffect(() => {
    if (!exchangeJobState.sourceType || !destinationType || !exchangeJobState.delimiter) return;

    const salesforceDelims =
      ["salesforce", "veevavault"].includes(destinationType) ||
      exchangeJobState.sourceType.toLowerCase() === "salesforce";
    const _delimiters = salesforceDelims ? salesforceDelimiters : delimiters;

    if (!_delimiters.some(({ value }) => value === exchangeJobState.delimiter))
      setExchangeJobState((prev) => ({
        ...prev,
        delimiter: salesforceDelims ? _delimiters[0].value : defaultDelimiter,
      }));
  }, [
    defaultDelimiter,
    delimiters,
    destinationType,
    exchangeJobState.delimiter,
    exchangeJobState.sourceType,
    salesforceDelimiters,
    setExchangeJobState,
  ]);

  useEffect(() => {
    if (destinationType && !isTableLoaded) {
      if (destinationType === "mysql") loadmysqlTables();
      else if (destinationType === "snowflake") {
        if (destination === "SNOWFLAKE") loadSnowflakeTables();
        else loadExternalSnowflakeTables();
      }
    }
    return () => {};
  }, [destination, destinationType, isTableLoaded, loadExternalSnowflakeTables, loadSnowflakeTables, loadmysqlTables]);

  const destContextValue = useMemo(
    () => ({
      restrictedObject,
      loadSnowflakeTables,
      loadExternalSnowflakeTables,
      loadmysqlTables,
    }),
    [restrictedObject, loadSnowflakeTables, loadExternalSnowflakeTables, loadmysqlTables]
  );

  const updatePreviousRef = useCallback(
    () => (newState) => setPreviousState((prev) => ({ ...prev, ...newState })),
    [setPreviousState]
  );

  return (
    <>
      <SmallToolbar>
        Operation
        <AddConnection loadLocationList={loadLocationList} />
      </SmallToolbar>
      <FormTile>
        <TextField
          select
          required
          fullWidth
          variant="outlined"
          sx={{ width: { xs: "100%", sm: "300px" } }}
          label="Operation"
          value={jobPurpose || JOB_PURPOSE_EXCHANGE}
          onChange={handleJobPurposeChange}
        >
          {JOB_PURPOSES.map(({ Id, Label }) => (
            <MenuItem key={Id} value={Id}>
              {Label}
            </MenuItem>
          ))}
        </TextField>
      </FormTile>

      <SmallToolbar>Source</SmallToolbar>
      <FormTile>
        <SourceForm
          locationList={locationList}
          source={source}
          sourceType={sourceType}
          sourcePath={sourcePath}
          sourcePathError={sourcePathError}
          queryError={queryError}
          query={query}
          delimiter={delimiter}
          filename={filename}
          fileType={fileType}
          loadingDropBoxValues={loadingDropBoxValues}
          customDelimeter={customDelimeter}
          destination={destination}
          resetFiles={resetFiles}
          targetPath={targetPath}
          setExchangeJobState={setExchangeJobState}
          exchangeJobState={exchangeJobState}
          previousState={previousState}
          fieldEnclosedBy={fieldEnclosedBy}
          encoding={encoding}
          hasHeaderRow={hasHeaderRow}
          loadSchemasClusters={loadSchemasClusters}
          runTimeState={runTimeState}
        />
      </FormTile>

      <SmallToolbar sx={{ mt: "12px" }}>Destination</SmallToolbar>
      <FormTile>
        <DestinationContext.Provider value={destContextValue}>
          <DestinationForm
            setExchangeJobState={setExchangeJobState}
            exchangeJobState={exchangeJobState}
            previousState={previousState}
            fieldLoading={fieldLoading}
            loadSchemasClusters={loadSchemasClusters}
          />
        </DestinationContext.Provider>
      </FormTile>

      {mapping && (
        <MappingPopup
          destinationType={destinationType}
          exchangeJobState={exchangeJobState}
          value={1}
          setExchangeJobState={setExchangeJobState}
          delimeter={delimiter === "custom" ? customDelimeter : delimiter}
          updatePreviousRef={updatePreviousRef}
          // If no change in previously selected options fields will get loaded again
          isFilePropertiesChanged={
            previousState.destination !== destination ||
            previousState.delimiter !== delimiter ||
            previousState.hasHeaderRow !== hasHeaderRow ||
            previousState.encoding !== encoding ||
            previousState.fieldEnclosedBy !== fieldEnclosedBy ||
            previousState.schemaName !== schemaName ||
            previousState.groupBy !== groupBy ||
            previousState.tableName !== tableName ||
            previousState.destinationAction !== destinationAction ||
            previousState.existingTableAction !== existingTableAction ||
            previousState.source !== source ||
            previousState.sourcePath !== sourcePath
          }
          existingFields={fields}
        />
      )}
    </>
  );
};

export default React.memo(SourceDestination);
